import { useLanguage } from "@/context/LanguageContext";
import { Seo } from "@/components/Seo";
import { Eyebrow } from "@/components/shared";
import { SITE } from "@/config/site";
import FareTerms from "@/components/FareTerms";

const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="text-xl font-bold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </div>
);

export default function BookingTerms() {
  const { t, cityName } = useLanguage();
  return (
    <>
      <Seo
        title={t("Booking Terms & Privacy | Jitendra Singh Tour Operator Service")}
        description={t("Read our published hire-rate terms, fuel billing, GST, trip extras, booking confirmation and privacy information. Final quotations are confirmed directly.")}
      />
      <section className="bg-[#071426]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <Eyebrow className="text-amber-400">{t("Booking Terms & Privacy")}</Eyebrow>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-white">{t("How our enquiry & quote process works")}</h1>
          <p className="mt-4 text-slate-300">{t("Read how quotes, booking confirmation and personal data are handled for our taxi services, car hire and cab journeys, including Tempo Traveller group travel. Sending an enquiry does not confirm a booking.")}</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
        <Section title={t("Enquiries and quotes")}>
          <p>{t("This website is an enquiry service. Submitting the form, tapping WhatsApp or calling us starts a conversation — it is not a confirmed booking. We respond with a quote and, if you choose to proceed, confirm the booking directly with you.")}</p>
          <p>{t("Our fleet pages publish standard local and outstation hire rates for the listed vehicles. The final trip quote combines the applicable hire rate, fuel billing, GST and trip extras for your itinerary. Availability and the complete quotation are confirmed directly before booking. The 9-seater Tempo Traveller remains available on request, with its price confirmed separately.")}</p>
        </Section>
        <Section title={t("What your final quote confirms")}>
          <p>{t("Your quote confirms the assigned vehicle, travel dates, itinerary, billable distance, hire charges, fuel billing and applicable extras. Share your passenger count, luggage and all stops so the team can confirm a suitable vehicle and the complete amount.")}</p>
          <p>{t("For the priced Tempo Travellers, seat totals include one driver: 13 seater means 12 passengers + 1 driver; 17 means 16 + 1; 20 means 19 + 1; 21 means 20 + 1; and 26 means 25 + 1. Confirm the 9-seater layout separately when enquiring.")}</p>
        </Section>
        <div className="mt-8"><FareTerms /></div>
        <Section title={t("Availability")}>
          <p>{t("Vehicle availability is confirmed before travel. For longer-distance and secondary destinations, trips are arranged on a prebooked basis — please plan these in advance. We do not promise instant on-demand pickup in every location.")}</p>
        </Section>
        <Section title={t("Payments")}>
          <p>{t("We do not take online payments through this website. There are no customer accounts. Payment terms, if any, are agreed directly with you at the time of booking.")}</p>
        </Section>
        <Section title={t("Your information (privacy)")}>
          <p>{t("When you submit an enquiry, we collect the details you provide — such as your name, callback number and trip details — so we can respond with a quote and arrange your travel. These details are emailed to our team and kept to handle your enquiry.")}</p>
          <p>{t("If you contact us by WhatsApp, your message is handled through WhatsApp. We use your information only to respond to your enquiry and arrange travel, and we do not sell it. To ask about your data, contact us at {email}.", { email: SITE.email })}</p>
        </Section>
        <Section title={t("Contact")}>
          <p>{t("{brand}, {address}. Phone / WhatsApp: {primary_phone} (primary), {alternate_phone} (alternate). Email: {email}.", { brand: SITE.brand, address: cityName(SITE.addressShort), primary_phone: SITE.phonePrimary.display, alternate_phone: SITE.phoneSecondary.display, email: SITE.email })}</p>
        </Section>
      </section>
    </>
  );
}
